import React from 'react'
import { useSelector } from 'react-redux'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'
import { Link } from 'react-router-dom'

import {
    CompanyTransferRequest,
    useCompanyTransferAcceptRequestMutation,
    useCompanyTransferGetRequestsForPartnerQuery,
    useCompanyTransferGetRequestsQuery,
    useCompanyTransferRejectRequestMutation,
} from '../../redux/api/web-api'
import { RootState } from '../../../partner/redux/store'
import { Table } from '../../uikit/Table'
import { dateLocalize } from '../../../lib/date-localize'
import { isPartnerDomain } from '../../../lib/currentDomain'
import useText from '../../hooks/useText'
import { useToast } from '../../uikit/ToastProvider'
import Text from '../../../ui/Text'

export const CompanyTransfersWidget = () => {
    const { partnerId } = useSelector((state: RootState) => state.user)

    const text = useText()
    const toast = useToast()

    const { data: transfers, isLoading: transfersIsLoading } = useCompanyTransferGetRequestsForPartnerQuery(
        { partnerId: partnerId ?? -1 },
        { skip: !partnerId }
    )

    const { data: supportTransfers, isLoading: supportTransfersIsLoading } = useCompanyTransferGetRequestsQuery()

    const [acceptTransfer] = useCompanyTransferAcceptRequestMutation()
    const [rejectTransfer] = useCompanyTransferRejectRequestMutation()

    const confirmApproveTransfer = (companyTransferRequest: CompanyTransferRequest) => {
        confirmDialog({
            header: text('lblApprovingOrgInvitation'),
            message: (
                <div className="flex flex-column gap-3">
                    <p>
                        <Text
                            code="msgTransferApprovingOrgInvitation"
                            values={{ organisation: companyTransferRequest.companyName }}
                        />
                    </p>
                    <p>{text('msgTransferApprovingOrgInvitationInfo')}</p>
                </div>
            ),
            accept: () => {
                if (!companyTransferRequest.companyId || !companyTransferRequest.toPartnerId) return

                acceptTransfer({
                    companyId: companyTransferRequest.companyId,
                    partnerId: companyTransferRequest.toPartnerId,
                })
                    .unwrap()
                    .then(() => {
                        toast.notifySuccess(text('msgTransferSuccessful'))
                    })
                    .catch(() => {
                        toast.notifyError(text('lblSomethingWentWrong'))
                    })
            },
            acceptLabel: text('btnApprove'),
            rejectLabel: text('btnCancel'),
        })
    }

    const confirmRejectTransfer = (companyTransferRequest: CompanyTransferRequest) => {
        confirmDialog({
            header: text('lblTransferRejectingOrgInvitation'),
            message: (
                <Text
                    code="msgTransferRejectingOrgInvitation"
                    values={{ organisation: companyTransferRequest.companyName }}
                />
            ),
            accept: () => {
                if (!companyTransferRequest.companyId || !companyTransferRequest.toPartnerId) return

                rejectTransfer({
                    companyId: companyTransferRequest.companyId,
                    partnerId: companyTransferRequest.toPartnerId,
                })
                    .unwrap()
                    .then(() => {
                        toast.notifySuccess(text('msgTransferRejected'))
                    })
                    .catch(() => {
                        toast.notifyError(text('lblSomethingWentWrong'))
                    })
            },
            acceptLabel: text('btnDeny'),
            rejectLabel: text('btnCancel'),
        })
    }

    return (
        <Table
            value={isPartnerDomain ? transfers : supportTransfers}
            dataKey="id"
            paginator
            rows={10}
            emptyMessage={text('msgNoPendingTransfersFound')}
            data-testid="transfers-widget"
            loading={transfersIsLoading || supportTransfersIsLoading}
        >
            <Column
                field="companyName"
                body={(rowData: CompanyTransferRequest) =>
                    isPartnerDomain ? (
                        rowData.companyName
                    ) : (
                        <Link className="pr-4" to={`/company/${rowData.companyId}`}>
                            {rowData.companyName}
                        </Link>
                    )
                }
                header={text('lblName')}
                sortable
            ></Column>
            <Column
                body={(rowData: CompanyTransferRequest) => dateLocalize(rowData.validTo)}
                header={text('lblValidUntil')}
                sortable
                field="validTo"
            ></Column>

            <Column header={text('lblInitiatedBy')} field="requestByName" sortable></Column>

            <Column
                hidden={isPartnerDomain}
                header={text('lblFromPartner')}
                sortable
                field="fromPartnerName"
                body={(rowData: CompanyTransferRequest) => (
                    <Link className="pr-4" to={`/partner/${rowData.fromPartnerId}`}>
                        {rowData.fromPartnerName}
                    </Link>
                )}
            ></Column>
            <Column
                hidden={isPartnerDomain}
                header={text('lblToPartner')}
                sortable
                field="toPartnerName"
                body={(rowData: CompanyTransferRequest) => (
                    <Link className="pr-4" to={`/partner/${rowData.toPartnerId}`}>
                        {rowData.toPartnerName}
                    </Link>
                )}
            ></Column>

            <Column
                body={(rowData: CompanyTransferRequest) => (
                    <div className="flex gap-2 justify-content-end">
                        <Button
                            size="small"
                            outlined
                            label={text('btnDeny')}
                            onClick={() => confirmRejectTransfer(rowData)}
                        />
                        <Button
                            size="small"
                            label={text('btnApprove')}
                            onClick={() => confirmApproveTransfer(rowData)}
                        />
                    </div>
                )}
            ></Column>
        </Table>
    )
}
